import React, { FC } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useOperator } from "@sinch/components/application/ConditionBuilder/useOperator";
import { useConditionCategories } from "@sinch/components/application/RateCondition/useConditionCategories";
import { useRateConditions } from "@sinch/components/application/RateCondition/useRateConditions";
import { useRateConditionPossibleValues } from "@sinch/components/application/RateCondition/useRateConditionPossibleValues";
import { useRateBuilderSettings } from "@sinch/components/application/RatelistBuilder/UtilitiesProviders/RateBuilderSettingsProvider";
import { RateConditionSchemaFields } from "@sinch/components/application/RatelistBuilder/ratelistBuilderSchema";
import { OverflowTooltip } from "@sinch/components/typography/OverflowTooltip/OverflowTooltip";

export const RateConditionOverview: FC<{ condition: RateConditionSchemaFields | null }> = ({ condition }) => {
  const { t } = useTranslation();
  const { rateType } = useRateBuilderSettings();
  const { getLabel } = useConditionCategories(rateType);
  const { getLabel: getConditionLabel } = useRateConditions(condition?.category);
  const { getValueLabel } = useRateConditionPossibleValues(condition);
  const { getOperatorLabel } = useOperator();

  if (condition === null) {
    return t("RateBuilder.noConditionOption");
  }

  const valString = getValueLabel(condition.value);
  const subjectLabel = getConditionLabel(condition.subjectId, condition.type);
  const categoryLabel = condition.category ? (getLabel(condition.category) ?? null) : null;

  const conditionText = (
    <Box
      sx={{
        textOverflow: "ellipsis",
        overflow: "hidden",
        display: "block",
        maxWidth: "100%",
        alignItems: "center",
        "& > *": { display: "inline", paddingRight: 0.5 },
      }}
    >
      <Typography sx={{ whiteSpace: "nowrap" }} variant="descriptionTinyBold">
        {categoryLabel}
      </Typography>
      <Typography sx={{}}>•</Typography>
      <Typography fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
        {subjectLabel}
      </Typography>
      {condition.operator !== null && (
        <Typography
          fontWeight={600}
          sx={(theme) => ({
            color: theme.palette.grey[700],
            whiteSpace: "nowrap",
          })}
        >
          {getOperatorLabel(condition.operator)}
        </Typography>
      )}
      <Typography fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
        {valString}
      </Typography>
    </Box>
  );
  return (
    <OverflowTooltip slotProps={{ tooltip: { sx: { "& *": { color: "white !important" } } } }} title={conditionText}>
      {conditionText}
    </OverflowTooltip>
  );
};
